import React, { useEffect, useState } from 'react';
import { Button, Box, Heading, useToast, Select, FormControl, FormLabel, Text } from '@chakra-ui/react';
import { collection, getDocs, writeBatch } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { useAuth } from '../../context/AuthContext';
import { LeagueId, Match } from '../../types';
import {
  getMatchIdsForMundialJornada,
  getMatchIdsForMundialKnockoutPhase,
  getMundialKnockoutMatches,
  getKnockoutPhaseLabel,
  MUNDIAL_GROUP_JORNADAS,
  parseLigaMxJornadaNumber,
  sortKnockoutPhases,
} from '../../services/matchdayService';

const LEAGUE_NAMES: Record<LeagueId, string> = {
  liga_mx: 'Liga BBVA MX',
  mundial_fifa: 'Mundial FIFA 2026',
};

const ActivarQuinielas: React.FC = () => {
  const toast = useToast();
  const { user } = useAuth();
  const [league, setLeague] = useState<LeagueId>('liga_mx');
  const [loading, setLoading] = useState(false);
  const [phases, setPhases] = useState<string[]>([]);
  const [selectedPhase, setSelectedPhase] = useState('');
  const [selectedJornada, setSelectedJornada] = useState('');
  const [selectedKnockoutPhase, setSelectedKnockoutPhase] = useState('');
  const [allMatches, setAllMatches] = useState<Match[]>([]);

  useEffect(() => {
    const fetchPhases = async () => {
      const matchesRef = collection(db, 'matches');
      const matchesSnap = await getDocs(matchesRef);
      const filtered = matchesSnap.docs
        .map((docSnap) => ({ id: docSnap.id, ...docSnap.data() } as Match))
        .filter((m) => (m.league ?? 'liga_mx') === league);

      setAllMatches(filtered);

      if (league === 'mundial_fifa') {
        setPhases([]);
        setSelectedPhase('');
        setSelectedJornada('');
        setSelectedKnockoutPhase('');
        return;
      }

      const uniquePhases = Array.from(
        new Set(
          filtered
            .map((m) => m.phase)
            .filter((phase) => phase && parseLigaMxJornadaNumber(phase) !== null)
        )
      ).sort((a, b) => (parseLigaMxJornadaNumber(a) ?? 0) - (parseLigaMxJornadaNumber(b) ?? 0));

      setPhases(uniquePhases);
      setSelectedPhase('');
      setSelectedJornada('');
      setSelectedKnockoutPhase('');
    };
    fetchPhases();
  }, [league]);

  const knockoutPhases =
    league === 'mundial_fifa'
      ? sortKnockoutPhases(
          Array.from(
            new Set(getMundialKnockoutMatches(allMatches).map((m) => getKnockoutPhaseLabel(m)))
          )
        )
      : [];

  const resolveMatchIds = (): string[] => {
    if (league === 'mundial_fifa') {
      if (selectedKnockoutPhase) {
        return getMatchIdsForMundialKnockoutPhase(allMatches, selectedKnockoutPhase);
      }
      const jornada = Number(selectedJornada);
      if (!jornada) return [];
      return getMatchIdsForMundialJornada(allMatches, jornada);
    }
    return allMatches.filter((m) => m.phase === selectedPhase).map((m) => m.id);
  };

  const handleActivateQuinielas = async () => {
    const hasSelection =
      league === 'mundial_fifa'
        ? selectedJornada || selectedKnockoutPhase
        : selectedPhase;
    if (!hasSelection) {
      toast({
        title: 'Selecciona una jornada',
        status: 'warning',
        duration: 2000,
      });
      return;
    }

    const matchIds = resolveMatchIds();
    if (matchIds.length === 0) {
      toast({
        title: 'Sin partidos',
        description: 'No hay partidos para la jornada seleccionada.',
        status: 'warning',
        duration: 3000,
      });
      return;
    }

    setLoading(true);
    try {
      const predictionsRef = collection(db, 'predictions');
      const predictionsSnap = await getDocs(predictionsRef);

      const batch = writeBatch(db);
      let updatedCount = 0;

      predictionsSnap.docs.forEach((docSnap) => {
        const prediction = docSnap.data();
        if (prediction.match_id && matchIds.includes(prediction.match_id)) {
          batch.update(docSnap.ref, { activo: 1 });
          updatedCount++;
        }
        if (Array.isArray(prediction.predictions)) {
          let changed = false;
          const newPredictions = prediction.predictions.map((p: { match_id: string; activo?: number }) => {
            if (matchIds.includes(p.match_id)) {
              if (p.activo !== 1) {
                changed = true;
                return { ...p, activo: 1 };
              }
            }
            return p;
          });
          if (changed) {
            batch.update(docSnap.ref, { predictions: newPredictions });
            updatedCount++;
          }
        }
      });

      await batch.commit();

      const label =
        league === 'mundial_fifa'
          ? selectedKnockoutPhase || `Jornada ${selectedJornada} — Fase de grupos`
          : selectedPhase;

      toast({
        title: 'Jornada activada',
        description: `Se activaron ${updatedCount} pronósticos de "${label}".`,
        status: 'success',
        duration: 4000,
      });
    } catch (error) {
      console.error('Error al activar quinielas:', error);
      toast({
        title: 'Error',
        description: 'No se pudieron activar las quinielas.',
        status: 'error',
        duration: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  if (!user || user.email !== 'htorres@teiker.mx') {
    return (
      <Box p={4}>
        <Heading size="md">No tienes acceso a esta página</Heading>
      </Box>
    );
  }


  return (
    <Box p={8} textAlign="center">
      <Heading size="lg" mb={2}>
        Activar Quinielas por Jornada
      </Heading>
      <Text mb={6} color="gray.600" fontSize="sm">
        {league === 'mundial_fifa'
          ? 'Activa los pronósticos capturados de una jornada de grupos o de una ronda eliminatoria.'
          : 'Activa los pronósticos de una jornada de Liga MX.'}
      </Text>

      <FormControl maxW="400px" mx="auto" mb={4}>
        <FormLabel>Liga / Torneo</FormLabel>
        <Select value={league} onChange={(e) => setLeague(e.target.value as LeagueId)} mb={2}>
          <option value="liga_mx">{LEAGUE_NAMES.liga_mx}</option>
          <option value="mundial_fifa">{LEAGUE_NAMES.mundial_fifa}</option>
        </Select>
      </FormControl>

      <FormControl maxW="400px" mx="auto" mb={4}>
        <FormLabel>Jornada</FormLabel>
        {league === 'mundial_fifa' ? (
          <>
            <Select
              placeholder="Jornada de grupos (opcional)"
              value={selectedJornada}
              onChange={(e) => {
                setSelectedJornada(e.target.value);
                setSelectedKnockoutPhase('');
              }}
              mb={2}
            >
              {MUNDIAL_GROUP_JORNADAS.map((j) => (
                <option key={j} value={String(j)}>
                  Jornada {j} — Fase de grupos
                </option>
              ))}
            </Select>
            <Select
              placeholder="Ronda eliminatoria (opcional)"
              value={selectedKnockoutPhase}
              onChange={(e) => {
                setSelectedKnockoutPhase(e.target.value);
                setSelectedJornada('');
              }}
            >
              {knockoutPhases.map((phase) => (
                <option key={phase} value={phase}>
                  {phase}
                </option>
              ))}
            </Select>
          </>
        ) : (
          <Select
            placeholder="Selecciona la jornada"
            value={selectedPhase}
            onChange={(e) => setSelectedPhase(e.target.value)}
          >
            {phases.map((phase) => (
              <option key={phase} value={phase}>
                {phase}
              </option>
            ))}
          </Select>
        )}
      </FormControl>

      {league === 'mundial_fifa' && (selectedJornada || selectedKnockoutPhase) && (
        <Text fontSize="sm" color="gray.600" mb={4}>
          {resolveMatchIds().length} partidos en esta selección
        </Text>
      )}

      <Button
        colorScheme="green"
        size="lg"
        onClick={handleActivateQuinielas}
        isLoading={loading}
        isDisabled={
          league === 'mundial_fifa'
            ? !selectedJornada && !selectedKnockoutPhase
            : !selectedPhase
        }
      >
        Activar Quinielas
      </Button>
    </Box>
  );
};

export default ActivarQuinielas;
